/**
 * Bridge OrderDesk landing folder "Wholesale" (pre-ops / not yet confirmed)
 * into a selected dated Packers folder view when delivery/dispatch can match,
 * and optionally surface undated Wholesale orders in a separate pending list.
 *
 * Confirmed pack requirements must never silently include these orders.
 */

import {
  classifyXeroOrderForFolder,
  getOrderBridgeKeys,
  getSelectedFolderMatchBounds,
  normalizeFlexibleOrderDate,
} from './xeroWholesaleFolderBridge.js';
import { getOrderDeliveryDate, getOrderDispatchDate } from '../utils/orderDateUtils.js';
import { fetchOrderDeskFolderOrders } from './orderDeskFolderOrdersService.js';

/** Exact OrderDesk landing folder for new wholesale orders awaiting admin confirmation. */
export const WHOLESALE_LANDING_FOLDER_NAME = 'Wholesale';
/** Canonical OrderDesk folder id for the Wholesale landing pen (confirmed by operator). */
export const WHOLESALE_LANDING_FOLDER_ID = 240791;
/** sessionStorage key shared by Packers + Production Planner for Mute pending (Wholesale). */
export const MUTE_WHOLESALE_PENDING_SESSION_KEY = 'lsc_mute_wholesale_pending_v1';

const CACHE_TTL_MS = 60_000;
let wholesaleLandingCache = { key: '', at: 0, orders: null };

export function isWholesaleLandingFolderId(folderId) {
  return String(folderId ?? '').trim() === String(WHOLESALE_LANDING_FOLDER_ID);
}

export function findWholesaleLandingFolder(folders = []) {
  const list = folders || [];
  // Prefer canonical OrderDesk folder id (stable if the display name ever drifts).
  const byId = list.find((folder) => isWholesaleLandingFolderId(folder?.id));
  if (byId) return byId;
  const exact = list.find(
    (folder) => String(folder?.name || '').trim() === WHOLESALE_LANDING_FOLDER_NAME,
  );
  if (exact) return exact;
  // Exact "Wholesale" only — never "Wholesale (Sent to Xero)" or other variants.
  return (
    list.find((folder) =>
      /^wholesale$/i.test(String(folder?.name || '').trim()),
    ) || null
  );
}

export function isWholesaleUnconfirmedOrder(order) {
  return Boolean(
    order?.unconfirmedPending
      || order?.linkedFromWholesalePending
      || order?.wholesalePendingUndated,
  );
}

function orderHasMatchableDate(order) {
  const deliveryIso = normalizeFlexibleOrderDate(
    getOrderDeliveryDate(order) || order?.deliveryDate || order?.delivery_date,
  );
  const dispatchIso = normalizeFlexibleOrderDate(
    getOrderDispatchDate(order) || order?.dispatchDate || order?.dispatch_date,
  );
  return Boolean(deliveryIso || dispatchIso);
}

/**
 * Re-home a Wholesale landing order onto the selected Packers folder for visibility
 * while preserving the real OD folder and marking it clearly unconfirmed.
 *
 * IDENTITY INVARIANT: Same as Xero bridge — do not invent a new order. Packed /
 * shipped stay keyed to source_id / OrderDesk id variants. These orders must not
 * enter confirmed weekly need / requirements totals.
 */
export function adaptWholesalePendingOrderForSelectedFolder(order, selectedFolder, match, { undated = false } = {}) {
  const sourceId = String(
    order?.source_id || order?.sourceId || order?.orderNumber || order?.orderId || '',
  ).replace(/^#/, '').trim();
  const orderDeskId = order?.order_id || order?.id || order?.orderId || null;
  return {
    ...order,
    orderId: order?.orderId || sourceId || orderDeskId,
    orderNumber: sourceId || order?.orderNumber,
    sourceId: sourceId || order?.sourceId,
    source_id: sourceId || order?.source_id,
    id: order?.id || orderDeskId || sourceId,
    order_id: order?.order_id || orderDeskId || null,
    order_number: order?.order_number || order?.orderNumber || sourceId || null,
    folderId: selectedFolder?.id ?? null,
    folderName: selectedFolder?.name ?? null,
    odFolderId: order?.folderId ?? null,
    odFolderName: order?.folderName || WHOLESALE_LANDING_FOLDER_NAME,
    unconfirmedPending: true,
    linkedFromWholesalePending: !undated,
    wholesalePendingUndated: Boolean(undated),
    wholesalePendingMatchQuality: undated ? 'undated' : (match?.quality || 'delivery'),
  };
}

async function loadWholesaleLandingOrders(landingFolder) {
  const key = String(landingFolder?.id ?? landingFolder?.name ?? '');
  if (
    wholesaleLandingCache.key === key
    && Date.now() - wholesaleLandingCache.at < CACHE_TTL_MS
    && Array.isArray(wholesaleLandingCache.orders)
  ) {
    return wholesaleLandingCache.orders;
  }
  const orders = await fetchOrderDeskFolderOrders({
    folder: landingFolder,
    dateField: 'delivery',
  });
  wholesaleLandingCache = { key, at: Date.now(), orders };
  return orders;
}

/**
 * @returns {Promise<{
 *   linkedOrders: object[],
 *   undatedOrders: object[],
 *   landingFolder: object|null,
 *   bounds: object|null,
 *   skippedReason?: string
 * }>}
 */
export async function fetchWholesalePendingForSelectedFolder({
  selectedFolder,
  folders = [],
} = {}) {
  const bounds = getSelectedFolderMatchBounds(selectedFolder?.name);
  if (!bounds) {
    return {
      linkedOrders: [],
      undatedOrders: [],
      landingFolder: null,
      bounds: null,
      skippedReason: 'Selected folder is not a dated OrderDesk folder.',
    };
  }

  const landingFolder = findWholesaleLandingFolder(folders);
  if (!landingFolder) {
    return {
      linkedOrders: [],
      undatedOrders: [],
      landingFolder: null,
      bounds,
      skippedReason: `Folder "${WHOLESALE_LANDING_FOLDER_NAME}" (id ${WHOLESALE_LANDING_FOLDER_ID}) was not found.`,
    };
  }

  // Do not bridge the landing folder into itself.
  if (
    isWholesaleLandingFolderId(selectedFolder?.id)
    || String(landingFolder.id) === String(selectedFolder?.id)
    || String(landingFolder.name || '').trim() === String(selectedFolder?.name || '').trim()
  ) {
    return {
      linkedOrders: [],
      undatedOrders: [],
      landingFolder,
      bounds,
      skippedReason: 'Selected folder is the Wholesale landing folder.',
    };
  }

  const landingOrders = await loadWholesaleLandingOrders(landingFolder);
  const linkedOrders = [];
  const undatedOrders = [];

  for (const order of landingOrders || []) {
    if (!orderHasMatchableDate(order)) {
      undatedOrders.push(
        adaptWholesalePendingOrderForSelectedFolder(order, selectedFolder, null, { undated: true }),
      );
      continue;
    }
    const match = classifyXeroOrderForFolder(order, bounds);
    if (!match.include) continue;
    linkedOrders.push(
      adaptWholesalePendingOrderForSelectedFolder(order, selectedFolder, match, { undated: false }),
    );
  }

  return { linkedOrders, undatedOrders, landingFolder, bounds };
}

export { getOrderBridgeKeys };

/** @visibleForTesting */
export function __resetWholesalePendingFolderBridgeCacheForTests() {
  wholesaleLandingCache = { key: '', at: 0, orders: null };
}
